import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { UserPlus, Mail, Lock, User, KeyRound, ArrowLeft } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fixtrack-25/artifacts/fon8nfqi_Disen%CC%83o%20sin%20ti%CC%81tulo.png";
const BG_IMAGE = "https://images.pexels.com/photos/3823025/pexels-photo-3823025.jpeg";

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error('Por favor, completa todos los campos requeridos');
      return;
    }
    
    if (!invitationCode) {
      toast.error('El código de invitación es obligatorio para registrarse');
      return;
    }
    
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setLoading(true);
    try {
      const user = await register(name, email, password, invitationCode);
      toast.success(`Bienvenido, ${user.name}`);
      navigate('/client');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-[40%] flex items-center justify-center p-8 bg-[#020617]">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-12">
            <img src={LOGO_URL} alt="Navalec" className="w-16 h-16 object-contain" />
            <div>
              <h1 className="font-rajdhani text-3xl font-bold text-white tracking-tight">NAVALEC</h1>
              <p className="text-sm text-sky-400 font-medium tracking-widest">SOLUTIONS</p>
            </div>
          </div>
          
          {/* Form */}
          <div>
            <h2 className="font-rajdhani text-2xl font-semibold text-white mb-2">
              Registro de Cliente
            </h2>
            <p className="text-slate-400 text-sm mb-8">
              Usa el código de invitación proporcionado por Navalec
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="invitation" className="text-slate-300 text-sm font-medium">
                  Código de Invitación <span className="text-rose-400">*</span>
                </Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="invitation"
                    type="text"
                    value={invitationCode}
                    onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
                    placeholder="XXXXXXXX"
                    maxLength={8}
                    className="pl-10 h-12 bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-600 focus:border-sky-500 font-mono uppercase tracking-widest"
                    data-testid="register-invitation-input"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Tu empresa debe haberte proporcionado este código
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-300 text-sm font-medium">
                  Nombre Completo <span className="text-rose-400">*</span>
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Tu nombre"
                    className="pl-10 h-12 bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-600 focus:border-sky-500"
                    data-testid="register-name-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300 text-sm font-medium">
                  Correo Electrónico <span className="text-rose-400">*</span>
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="pl-10 h-12 bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-600 focus:border-sky-500"
                    data-testid="register-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300 text-sm font-medium">
                  Contraseña <span className="text-rose-400">*</span>
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="pl-10 h-12 bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-600 focus:border-sky-500"
                    data-testid="register-password-input"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-sky-600 hover:bg-sky-500 text-white font-medium uppercase tracking-wider neon-glow-hover mt-8"
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Registrando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    Crear Cuenta
                  </span>
                )}
              </Button>
            </form>
            
            <div className="mt-8 pt-6 border-t border-slate-800">
              <p className="text-slate-400 text-sm text-center">
                <Link 
                  to="/login" 
                  className="text-sky-400 hover:text-sky-300 font-medium inline-flex items-center gap-1"
                  data-testid="go-to-login-link"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Volver al inicio de sesión
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Right side - Image */}
      <div className="hidden lg:flex lg:w-[60%] relative">
        <img 
          src={BG_IMAGE} 
          alt="Industrial" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-[#020617] via-[#020617]/80 to-transparent" />
        <div className="absolute inset-0 bg-sky-500/10" />
        <div className="relative z-10 flex flex-col justify-center items-end p-12 text-white text-right">
          <h1 className="font-rajdhani text-5xl font-bold tracking-tight mb-4">
            ACCEDE A TU<br />
            <span className="text-sky-400">PORTAL DE</span><br />
            MANTENIMIENTO
          </h1>
          <p className="text-lg text-slate-300 max-w-md">
            Consulta el estado de tus máquinas, trabajos realizados y crea incidencias cuando lo necesites.
          </p>
        </div>
      </div>
    </div>
  );
}
